'use client';

import { useEffect, useRef, useMemo } from 'react';
import type { UIMessage } from 'ai';
import { isToolUIPart } from 'ai';
import { SparkleIcon, LightbulbIcon, ChevronLeftIcon } from '../search/icons';
import AgentChatMessage, { stripSuggestions } from './AgentChatMessage';
import ChatInput from '../chat/ChatInput';
import { useClientConfig } from '@/lib/clients/ClientContext';

/**
 * True while the shopper is waiting and nothing on screen says so: after
 * sending, before the reply exists, and after the search, before its first
 * words. Measured on kostaboda (2026-11-03): ~1.5s blank, ~0.7s search
 * spinner, ~1.7s blank, then text. The founder saw no sign of life at all.
 * A running search is excluded: it has its own «Αναζήτηση προϊόντων...».
 */
function isAwaitingReplyText(messages: UIMessage[], isLoading: boolean): boolean {
  if (!isLoading) return false;
  const last = messages[messages.length - 1];
  if (!last || last.role !== 'assistant') return true;
  // Newest part first: words after the last search → no wait; a search still
  // running → its own indicator; a finished search with nothing after it → wait.
  for (let i = last.parts.length - 1; i >= 0; i--) {
    const part = last.parts[i];
    if (part.type === 'text') {
      if (stripSuggestions(part.text)) return false;
    } else if (isToolUIPart(part)) {
      return part.state !== 'input-streaming' && part.state !== 'input-available';
    }
  }
  return true;
}

interface AgentChatSidebarProps {
  messages: UIMessage[];
  isLoading: boolean;
  suggestions: string[];
  onSendMessage: (text: string) => void;
  onChipClick: (chip: string) => void;
  /** Set only when rendered as the phone slide-over — shows a collapse control
   *  that returns the shopper to the products. Absent at md+, where the panel
   *  is a permanent column and there is nothing to collapse. */
  onCollapse?: () => void;
  /** Text of that control (phone only), e.g. «Προϊόντα (30)». Without it the
   *  control is a bare arrow. */
  collapseLabel?: string;
}

export default function AgentChatSidebar({
  messages,
  isLoading,
  suggestions,
  onSendMessage,
  onChipClick,
  onCollapse,
  collapseLabel,
}: AgentChatSidebarProps) {
  const clientConfig = useClientConfig();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const prevLenRef = useRef(messages.length);
  const rafRef = useRef(0);

  // Client-branded chat avatar (e.g. syfak's Αριάδνη) replaces the sparkle in
  // the header. Relative paths are apiOrigin-prefixed for cross-origin embeds.
  const chatAvatarUrl = clientConfig.ui.chatAvatarUrl;
  const chatAvatarSrc = chatAvatarUrl
    ? chatAvatarUrl.startsWith('/')
      ? `${clientConfig.apiOrigin ?? ''}${chatAvatarUrl}`
      : chatAvatarUrl
    : undefined;

  // Scroll: smooth for new messages, instant (throttled) during streaming
  useEffect(() => {
    const isNewMessage = messages.length !== prevLenRef.current;
    prevLenRef.current = messages.length;

    if (isNewMessage) {
      // New message added: scroll immediately with animation
      cancelAnimationFrame(rafRef.current);
      rafRef.current = 0;
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    } else {
      // Streaming token update: throttle to one scroll per frame
      if (!rafRef.current) {
        rafRef.current = requestAnimationFrame(() => {
          messagesEndRef.current?.scrollIntoView({ behavior: 'instant' });
          rafRef.current = 0;
        });
      }
    }
  }, [messages]);

  // Cleanup rAF on unmount
  useEffect(() => () => cancelAnimationFrame(rafRef.current), []);

  const lastAssistantIdx = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].role === 'assistant') return i;
    }
    return -1;
  }, [messages]);

  const awaitingReplyText = useMemo(() => isAwaitingReplyText(messages, isLoading), [messages, isLoading]);

  return (
    <div className="w-full md:w-[340px] flex-shrink-0 flex flex-col h-full md:border-l border-[#E5E7EB] bg-white">
      {/* Header — aligned with product grid header (64px) */}
      <div className="h-[64px] flex items-center justify-between gap-3 px-6 flex-shrink-0 border-b-[0.5px] border-[#D8D8D8]">
        <div className="flex items-center gap-3 flex-shrink-0 min-w-0">
          {chatAvatarSrc ? (
            <img
              src={chatAvatarSrc}
              alt=""
              className="w-8 h-8 rounded-full object-cover flex-shrink-0"
            />
          ) : (
            <SparkleIcon className="w-[29px] h-6 flex-shrink-0" />
          )}
          <h3 className="text-[18px] font-medium font-gotham text-[#101828] leading-[28px] truncate">
            {clientConfig.ui.strings.aiAssistantTitle}
          </h3>
        </div>
        {onCollapse && (collapseLabel ? (
          <button
            type="button"
            onClick={onCollapse}
            className="flex items-center gap-1.5 h-8 pl-3 pr-2 flex-shrink-0 border border-[#C0C4CC] text-[13px] font-gotham font-medium text-text-primary whitespace-nowrap hover:border-brand hover:text-brand transition-colors cursor-pointer"
          >
            {collapseLabel}
            <ChevronLeftIcon className="w-3.5 h-3.5 rotate-180" />
          </button>
        ) : (
          <button
            type="button"
            onClick={onCollapse}
            aria-label="Επιστροφή στα προϊόντα"
            className="-mr-1 w-8 h-8 flex items-center justify-center flex-shrink-0 text-text-primary hover:text-brand transition-colors cursor-pointer"
          >
            <ChevronLeftIcon className="w-4 h-4 rotate-180" />
          </button>
        ))}
      </div>

      {/* Messages area */}
      <div className="flex-1 overflow-y-auto overscroll-contain custom-scrollbar p-4 flex flex-col gap-3">
        {messages.length === 0 && (
          <div className="flex flex-col items-start gap-2">
            <div className="max-w-[85%] rounded-2xl rounded-bl-md px-4 py-2.5 bg-[#F3F4F6]">
              <p className="text-[13px] font-gotham text-text-primary leading-[20px]">
                <LightbulbIcon className="w-5 h-5 inline-block mr-1.5 align-text-bottom" />
                {clientConfig.ui.strings.aiAssistantGreeting}
              </p>
            </div>
          </div>
        )}

        {messages.map((msg, idx) => (
          <AgentChatMessage
            key={msg.id}
            message={msg}
            suggestions={idx === lastAssistantIdx ? suggestions : undefined}
            onChipClick={idx === lastAssistantIdx ? onChipClick : undefined}
            isLatestAssistant={idx === lastAssistantIdx}
          />
        ))}

        {/* Typing dots — an assistant bubble with no words yet. Dots in the
            highlight colour: kostaboda's turquoise is too pale on the grey. */}
        {awaitingReplyText && (
          <div className="flex flex-col items-start animate-fadeIn" role="status" aria-label="Ο βοηθός γράφει">
            <div className="rounded-2xl rounded-bl-md px-4 py-3 bg-[#F3F4F6] inline-flex gap-1 text-highlight">
              <span className="w-1.5 h-1.5 bg-current rounded-full animate-bounce [animation-delay:0ms]" />
              <span className="w-1.5 h-1.5 bg-current rounded-full animate-bounce [animation-delay:150ms]" />
              <span className="w-1.5 h-1.5 bg-current rounded-full animate-bounce [animation-delay:300ms]" />
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <ChatInput onSend={onSendMessage} disabled={isLoading} autoFocus />
    </div>
  );
}
